import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { ShieldCheck, Pencil, Users as UsersIcon, Trash2, Plus, Power, PowerOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { DataTable } from '../../components/DataTable';
import { useServiceDesks } from '../../hooks/useServiceDesks';
import AppContext from '../../contexts/AppContext';
import { parseDeskAccess } from '../../utils/deskAccess';
import { useValidationGroups, useDeleteGroup, useUpdateGroup } from '../../hooks/useValidationGroups';
import type { ValidationGroup } from '../../api/validationGroupsApi';
import { GroupFormDialog } from './components/GroupFormDialog';
import { GroupMembersDialog } from './components/GroupMembersDialog';

const AdminGroups = () => {
  const { t } = useTranslation();
  const { serviceDesks } = useServiceDesks();
  const { currentUser } = useContext(AppContext) as {
    currentUser: { role?: string; serviceDeskId?: number | string | (number | string)[] } | null;
  };

  // Mirrors the backend rule: offering a desk the caller cannot manage would only produce
  // a 403 on the statuses and transitions calls, and an empty dropdown with no explanation.
  const manageableDesks = useMemo(() => {
    const { all, ids } = parseDeskAccess(currentUser?.serviceDeskId);
    const seesEveryDesk = currentUser?.role === 'superadmin' || all;
    return serviceDesks.filter((sd) => seesEveryDesk || ids.has(sd.id));
  }, [serviceDesks, currentUser]);
  const { data: groups, isLoading, error } = useValidationGroups();
  const deleteGroup = useDeleteGroup();
  const updateGroup = useUpdateGroup();

  const deskName = useMemo(() => {
    const map = new Map<number, string>();
    serviceDesks.forEach((sd) => map.set(sd.id, sd.name));
    return map;
  }, [serviceDesks]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ValidationGroup | null>(null);
  const [membersGroup, setMembersGroup] = useState<ValidationGroup | null>(null);
  const [toDelete, setToDelete] = useState<ValidationGroup | null>(null);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (g: ValidationGroup) => { setEditing(g); setFormOpen(true); };

  const columns = useMemo<ColumnDef<ValidationGroup>[]>(() => [
    { accessorKey: 'name', header: t('Name'), cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
    // These columns render derived data: without `accessorFn` the CSV export, which reads
    // `row.getValue(id)`, exports empty cells.
    {
      id: 'desk',
      header: t('Service desk'),
      accessorFn: (group) => deskName.get(group.service_desk_id) ?? String(group.service_desk_id),
      cell: ({ row }) => <span className="text-sm">{deskName.get(row.original.service_desk_id) ?? row.original.service_desk_id}</span>,
    },
    {
      id: 'status',
      header: t('Status'),
      // The bare id says nothing to a business user; the name comes from Jira, the id
      // stays visible as the value actually stored on the group.
      accessorFn: (group) => (group.status_name ? `${group.status_name} (${group.status_id})` : String(group.status_id)),
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.status_name ?? '—'}{' '}
          <span className="font-mono text-xs text-gray-500">({row.original.status_id})</span>
        </span>
      ),
    },
    {
      id: 'members',
      header: t('Members'),
      // Count on screen, names in the export: a CSV of "3" is not actionable.
      accessorFn: (group) => group.member_names.join(', '),
      cell: ({ row }) => (
        <span className="text-sm" title={row.original.member_names.join(', ')}>
          {row.original.member_count}
        </span>
      ),
    },
    {
      accessorKey: 'is_active',
      header: t('Active'),
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
            row.original.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {row.original.is_active ? t('Active') : t('Inactive')}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-1">
          <button onClick={() => setMembersGroup(row.original)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title={t('Members')}>
            <UsersIcon className="w-4 h-4" />
          </button>
          <button onClick={() => openEdit(row.original)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title={t('Edit')}>
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => updateGroup.mutate({ id: row.original.id, data: { isActive: !row.original.is_active } })}
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
            title={row.original.is_active ? t('Deactivate') : t('Activate')}
          >
            {row.original.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
          </button>
          <button onClick={() => setToDelete(row.original)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title={t('Delete')}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [t, deskName, updateGroup]);

  return (
    <div className="container mx-auto p-6 space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>{t('Validation groups')}</CardTitle>
                <p className="text-sm text-gray-500">{t('Manage who can validate tickets at each status')}</p>
              </div>
            </div>
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-1" />
              {t('New group')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">{(error as Error).message}</p>
          ) : (
            <DataTable data={groups ?? []} columns={columns} emptyMessage={t('No group')} pageSize={20} showPagination={false} />
          )}
        </CardContent>
      </Card>

      <GroupFormDialog
        open={formOpen}
        group={editing}
        serviceDesks={manageableDesks.map((sd) => ({ id: sd.id, name: sd.name }))}
        onClose={() => setFormOpen(false)}
      />

      <GroupMembersDialog group={membersGroup} onClose={() => setMembersGroup(null)} />

      <Dialog open={toDelete != null} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Delete group')}</DialogTitle>
            <DialogDescription>
              {t('This removes the group and its members. This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm">{toDelete?.name}</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setToDelete(null)}>{t('Cancel')}</Button>
            <Button
              variant="destructive"
              disabled={deleteGroup.isPending}
              onClick={() => toDelete && deleteGroup.mutate(toDelete.id, { onSuccess: () => setToDelete(null) })}
            >
              {deleteGroup.isPending ? t('Deleting…') : t('Delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGroups;
